import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Building2, Briefcase, MessageCircle, Copy, Save, Loader2, Flame } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_vertical: [] });
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/leads"),
        api.get("/leads/summary"),
      ]);
      setLeads(a.data);
      setSummary(s.data);
    } catch { toast.error("Failed to load leads"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// lead inbox</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          Who wants to talk?
        </h1>
        <p className="mt-3 text-bone/60 max-w-2xl">
          Every published article gets a landing page. Form submissions land here, auto-classified
          by Lingent (vertical, role, intent score 0-100) with a personalised reply pre-drafted.
        </p>

        {/* Status KPIs */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-6 gap-px bg-hairline border border-hairline">
          <KPI label="Total" v={summary.total || 0} testid="kpi-total" />
          {STATUSES.map((s) => (
            <KPI key={s} label={s} v={summary[s] || 0} accent={s === "new"} testid={`kpi-${s}`} />
          ))}
        </div>

        {/* Vertical breakdown */}
        {summary.by_vertical?.length > 0 && (
          <div className="mt-8">
            <div className="label-mono text-bone/60 mb-2">// where leads come from</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {summary.by_vertical.slice(0, 8).map((v) => (
                <div key={v.vertical} className="border border-hairline bg-surface p-4" data-testid={`vertical-${v.vertical}`}>
                  <div className="font-display text-lg font-bold tracking-tightest">{v.vertical}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="label-mono text-bone/50 text-[10px]">{v.count} leads</div>
                    <div className="flex items-center gap-1 label-mono text-[10px] text-safety">
                      <Flame className="w-3 h-3" /> avg {v.avg_intent}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter chips */}
        <div className="mt-10 flex items-center gap-2 flex-wrap border-b border-hairline pb-3">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`lead-filter-${s}`}
              className={`label-mono px-3 py-1.5 border ${
                filter === s
                  ? "bg-bone text-ink border-bone"
                  : "border-hairline text-bone/70 hover:text-bone"
              }`}
            >
              {s} {s !== "all" && summary[s] !== undefined ? `(${summary[s]})` : ""}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 border border-hairline overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead className="bg-surface label-mono text-bone/60 text-[10px]">
              <tr>
                <th className="text-left p-3">Captured</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Company</th>
                <th className="text-left p-3">Vertical</th>
                <th className="text-left p-3">Role</th>
                <th className="text-right p-3">Intent</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">UTM Campaign</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-bone/50">No leads yet. Publish an article and share its landing-page link.</td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-hairline hover:bg-surface" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3 label-mono text-[10px] text-bone/50">{String(l.captured_at).slice(0, 16)}</td>
                  <td className="p-3 font-display font-bold tracking-tightest">{l.name}</td>
                  <td className="p-3">{l.company || "—"}</td>
                  <td className="p-3 label-mono text-[10px]">{l.ai_vertical || "—"}</td>
                  <td className="p-3 label-mono text-[10px]">{l.ai_role_type || l.role || "—"}</td>
                  <td className="p-3 text-right">
                    <span className={`label-mono px-2 py-0.5 ${
                      l.ai_intent_score >= 70 ? "bg-safety text-white"
                      : l.ai_intent_score >= 40 ? "bg-klein text-white"
                      : "bg-surface-2 text-bone/70"
                    }`}>{l.ai_intent_score || 0}</span>
                  </td>
                  <td className="p-3 label-mono text-[10px]">{l.status}</td>
                  <td className="p-3 label-mono text-[10px] text-bone/50">{l.utm_campaign || "—"}</td>
                  <td className="p-3">
                    <Button size="sm" variant="outline"
                      onClick={() => setDetail(l)}
                      data-testid={`lead-open-${l.id}`}
                      className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-8">
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <LingentFloatingIcon onOpen={() => {}} />
      <LeadDetailDialog
        lead={detail}
        onClose={() => setDetail(null)}
        onSaved={() => { setDetail(null); load(); }}
      />
    </div>
  );
}

const KPI = ({ label, v, accent, testid }) => (
  <div className="bg-ink p-5" data-testid={testid}>
    <div className="label-mono text-bone/50">{label}</div>
    <div className={`font-display text-3xl font-black mt-1 ${accent ? "text-safety" : "text-bone"}`}>{v}</div>
  </div>
);

function LeadDetailDialog({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.status || "new");
      setNotes(lead.admin_notes || "");
      setReply(lead.ai_suggested_reply || "");
    }
  }, [lead]);

  if (!lead) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, { status, admin_notes: notes });
      toast.success("Lead updated");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const copyReply = async () => {
    try { await navigator.clipboard.writeText(reply); toast.success("Reply copied"); }
    catch { toast.message("Clipboard blocked"); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-ink border-hairline text-bone max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-bone flex items-center gap-2">
            {lead.name} <span className="text-bone/40 text-sm font-sans">· {lead.email}</span>
          </DialogTitle>
          <DialogDescription className="text-bone/60 text-xs">
            Captured {String(lead.captured_at).slice(0, 16)} via landing page <code>{lead.slug}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Info icon={Building2} label="Company" v={lead.company || "—"} />
          <Info icon={Briefcase} label="Role" v={lead.role || "—"} />
          <Info icon={Flame} label="AI vertical" v={lead.ai_vertical || "—"} />
          <Info icon={Flame} label="AI role" v={lead.ai_role_type || "—"} />
          <Info icon={Flame} label="Intent score" v={`${lead.ai_intent_score || 0} / 100`} />
          <Info icon={Mail} label="UTM source" v={lead.utm_source || "—"} />
        </div>

        {lead.message && (
          <div className="border-l-2 border-safety bg-surface p-3 mt-3">
            <div className="label-mono text-[10px] text-safety mb-1">// THEIR MESSAGE</div>
            <p className="text-bone/80 text-sm whitespace-pre-wrap">{lead.message}</p>
          </div>
        )}

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="label-mono text-bone/70">AI-suggested reply</Label>
            <Button size="sm" variant="ghost" onClick={copyReply}
              data-testid="lead-copy-reply"
              className="rounded-none label-mono h-7">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
          <Textarea
            rows={5}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            data-testid="lead-reply-textarea"
            className="rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
          />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label className="label-mono text-bone/70">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 rounded-none bg-surface border-hairline" data-testid="lead-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-surface border-hairline">
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-mono text-bone/70">Admin notes</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="lead-notes-textarea"
            className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein"
          />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
          <Button variant="outline" onClick={onClose}
            className="rounded-none border-hairline bg-transparent label-mono">Close</Button>
          <Button onClick={save} disabled={saving} data-testid="lead-save-btn"
            className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ icon: Icon, label, v }) => (
  <div className="flex items-start gap-2 border border-hairline bg-surface p-3">
    <Icon className="w-4 h-4 text-bone/50 mt-0.5" />
    <div>
      <div className="label-mono text-[10px] text-bone/50">{label}</div>
      <div className="text-bone/90 font-sans">{v}</div>
    </div>
  </div>
);
